import { FC, useCallback, useContext, useMemo, useState } from "react";

import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Button, Buttons, Link, Message, Messages, Panel, Table, Tooltip, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Body, Title } from "capitalrx-components-2.0/lib/typography";
import {
  Datebox,
  DateSerializer,
  Form,
  RadioGroup,
  SubmitButton,
  Textbox,
  useForm,
} from "capitalrx-components-2.0/lib/forms";

import { BackLink } from "@/components";
import { getCriteriaSourceDocumentsAPI, getCriteriaDocumentDownloadUrlAPI } from "@/api";
import { CriteriaSourceDocument } from "@/api/interfaces";
import { ThemeSelectorContext } from "@/context";
import { getThemeButton, getThemeURL } from "@/utils";

import styles from "./CriteriaPage.module.scss";

type SearchBy = "gpi" | "ndc" | "document_name";
const searchMap = {
  gpi: "GPI",
  ndc: "NDC",
};

interface CriteriaSearchForm {
  search_value: string;
  effective_from: string;
  effective_to: string;
}

export const CriteriaPage: FC = () => {
  const { primary_color, primary_hover_color, secondary_hover_color } = useContext(ThemeSelectorContext);

  const [searchBy, setSearchBy] = useState<SearchBy>("gpi");
  const [results, setResults] = useState<CriteriaSourceDocument.CriteriaSourceDocument[] | null>(null);
  const [resultCount, setResultCount] = useState<number>(0);
  const [dateError, setDateError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<boolean>(false);
  const [downloadingUuid, setDownloadingUuid] = useState<number | null>(null);

  const form = useForm<CriteriaSearchForm>();

  const { twelve, isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);

  const StackOrShelf = isCompressed ? Stack : Shelf;

  const searchFieldLabel = searchMap[searchBy] ?? "Document name";

  const handleSearch = useCallback(async (data: CriteriaSearchForm) => {
    setDateError(null);
    setApiError(false);

    const hasFrom = !!data.effective_from?.trim();
    const hasTo = !!data.effective_to?.trim();
    if (hasFrom !== hasTo) {
      setDateError("Both Effective From and Effective To are required if one is entered.");
      return;
    }

    const payload: CriteriaSourceDocument.CriteriaSourceDocumentListRequest = {};
    if (searchBy === "gpi") {
      payload.drug_gpis = [data.search_value];
    } else if (searchBy === "ndc") {
      payload.drug_ndcs = [data.search_value];
    } else if (searchBy === "document_name") {
      payload.document_name = data.search_value;
    }
    if (hasFrom) payload.effective_date = data.effective_from;

    try {
      const response = await getCriteriaSourceDocumentsAPI(payload);
      setResults(response.results);
      setResultCount(Number(response.result_count));
    } catch {
      setApiError(true);
      setResults(null);
      setResultCount(0);
    }
  }, [searchBy]);

  const handleClear = useCallback(() => {
    form.reset();
    setResults(null);
    setResultCount(0);
    setDateError(null);
    setApiError(false);
  }, [form]);

  const handleDownload = useCallback(async (id: number, name: string) => {
    setDownloadingUuid(id);
    try {
      const { url } = await getCriteriaDocumentDownloadUrlAPI({ id });
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${name}.pdf`;
      anchor.target = "_blank";
      anchor.click();
    } finally {
      setDownloadingUuid(null);
    }
  }, []);

  const tableRows = useMemo(() => (results ?? []).map((doc) => ({
    id: String(doc.id),
    data: doc,
  })), [results]);

  const extractNdc = (value: string): string => {
    if (value.includes("'ndc'")){
      try {
        // Best effort json parsing of our string blob
        const parsed = JSON.parse(value.replace(/'/g, '"').replace(/None/g, "null"));
        return parsed?.ndc ?? value;
      } catch {
        return value;
      }
    }
    return value;
  };

  const dataSource = useSimpleTableDataSource(tableRows);

  return (
    <div className={styles.CriteriaPageContainer}>
      <BackLink linkText="New Prior Authorization Case" to={getThemeURL("PA_CREATE")} />
      <Stack spacing="large">
        <Stack spacing="mini">
          <Title>Prior Authorization Standard Criteria</Title>
          <Body>Search and view prior authorization criteria for drugs by GPI, NDC, or document name</Body>
        </Stack>

        <Message appearance={Messages.Informational} visible className={styles.CriteriaInfoBanner}>
          <StackOrShelf spacing="small">
            <Body size="small">
              The criteria shown here represent standard requirements for each drug. Individual member benefits may vary.
              To access the exact criteria, start a prior authorization (PA) request for the member and drug.
            </Body>
            {!isCompressed && <Shelf.Spacer />}
            <Link to={getThemeURL("PA_CREATE")} className={styles.CriteriaStartPALink}>
              Start PA Request
            </Link>
          </StackOrShelf>
        </Message>

        <Form form={form} onSubmit={handleSearch}>
          <Stack spacing="large">
            <Stack>
              <RadioGroup
                label="Search By"
                value={searchBy}
                onChange={(value) => {
                  setSearchBy(value as SearchBy);
                  form.setValue("search_value", "");
                }}
                orientation="horizontal"
              >
                <RadioGroup.Option value="gpi">GPI</RadioGroup.Option>
                <RadioGroup.Option value="ndc">NDC</RadioGroup.Option>
                <RadioGroup.Option value="document_name">Document name</RadioGroup.Option>
              </RadioGroup>

              <Grid>
                <Grid.Span columns={twelve}>
                  <Textbox.Field
                    label={searchFieldLabel}
                    name="search_value"
                    required
                  />
                </Grid.Span>
              </Grid>

              {dateError && (
                <Message appearance={Messages.Error} visible>{dateError}</Message>
              )}
              <Grid>
                <Grid.Span columns={6}>
                  <Datebox.Field
                    zone="local"
                    serializer={DateSerializer.Literal}
                    label="Effective From"
                    name="effective_from"
                    instructions="mm/dd/yyyy"
                  />
                </Grid.Span>
                <Grid.Span columns={6}>
                  <Datebox.Field
                    zone="local"
                    serializer={DateSerializer.Literal}
                    label="Effective To"
                    name="effective_to"
                    instructions="mm/dd/yyyy"
                  />
                </Grid.Span>
              </Grid>
            </Stack>

            {apiError && (
              <Message appearance={Messages.Error} visible>Failed to retrieve records. Try a new search.</Message>
            )}
            <StackOrShelf spacing="small">
              <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)}>
                Search
              </SubmitButton>
              {!form.formState.isSubmitting && (
                <Button
                  appearance={getThemeButton("secondary", primary_color, secondary_hover_color)}
                  onClick={handleClear}
                >
                  Clear
                </Button>
              )}
              <Shelf.Spacer />
            </StackOrShelf>
          </Stack>
        </Form>

        {results && (
          <Panel className={styles.PanelWrapper}>
            <Table dataSource={dataSource} className={styles.CriteriaTableWrapper}>
              <Table.Custom name="criteria_attachment_title" title="Document" initialWidth="span" minWidth="fit">
                {(value: unknown, _id, row) => {
                  const docRow = row as { data: CriteriaSourceDocument.CriteriaSourceDocument };
                  const name = (value as string | null) ?? docRow.data.criteria_attachment_filename;
                  return (
                    <Tooltip alignment="top" content={name}>
                      {(ref) => (
                        <div ref={ref} className={styles.CriteriaDocumentNameTruncated} tabIndex={0}>{name}</div>
                      )}
                    </Tooltip>
                  );
                }}
              </Table.Custom>
              <Table.Custom name="drug_gpi_multi_source_pairs" title="GPI" minWidth={130}>
                {(value: unknown) => {
                  const text = ((value as string[]) ?? []).join(", ");
                  return (
                    <Tooltip content={text} alignment="top">
                      {(ref) => (
                        <div ref={ref} tabIndex={0}>
                          {text}
                        </div>
                      )}
                    </Tooltip>
                  );
                }}
              </Table.Custom>
              <Table.Custom name="drug_ndcs" title="NDC" minWidth={110}>
                {(value: unknown) => {
                  const text = ((value as string[]) ?? []).map(extractNdc).join(", ");
                  return (
                    <Tooltip content={text} alignment="top">
                      {(ref) => (
                        <div ref={ref} tabIndex={0}>
                          {text}
                        </div>
                      )}
                    </Tooltip>
                  );
                }}
              </Table.Custom>
              <Table.Custom name="effective_date" title="Effective Date" minWidth={110}>
                {(value: unknown) => {
                  const text = ((value as string) ?? []);
                  return (
                    <Tooltip content={text} alignment="top">
                      {(ref) => (
                        <div ref={ref} tabIndex={0}>
                          {text}
                        </div>
                      )}
                    </Tooltip>
                  );
                }}
              </Table.Custom>
              <Table.Custom name="id" title="Download" initialWidth={86} lock="right">
                {(value: unknown, _id, row) => {
                  const id = value as number;
                  const docRow = row as { data: CriteriaSourceDocument.CriteriaSourceDocument };
                  return (
                    <Tooltip alignment="top" content="Download">
                      {(ref) => (
                        <Button
                          ref={ref}
                          appearance={Buttons.Functional.Tertiary.Small}
                          icon="download"
                          disabled={downloadingUuid === id}
                          onClick={() => handleDownload(id, docRow.data.criteria_attachment_filename)}
                          style={{ width: "62px" }}
                        >
                        </Button>
                      )}
                    </Tooltip>
                  );
                }}
              </Table.Custom>
            </Table>
          <Body size="small">
            Showing {results.length > 0 ? `1-${results.length}` : "0"} of {resultCount} result{resultCount !== 1 ? "s" : ""}
          </Body>
          </Panel>
        )}
      </Stack>
    </div>
  );
};

export default CriteriaPage;
